/**
 * Los cuatro botones de respuesta del móvil.
 *
 * **Ocupan toda la pantalla entre los cuatro** (§7.3.3). No es estética: en un aula se responde con
 * el móvil en una mano, a veces de pie, mirando al proyector y no a la pantalla. Un botón pequeño
 * en esas condiciones es un fallo de puntería, no de conocimiento.
 *
 * **El texto de la opción se muestra siempre**, no solo cuando están barajadas. §7.3.3 lo dejaba a
 * la referencia común del proyector, y eso da por hecho que todo el mundo lo ve: quien se sienta al
 * fondo, quien ve mal de lejos o quien tiene el reflejo de una ventana encima no lo ven. La ficha
 * se reduce un poco para hacerle sitio, y el objetivo táctil sigue muy por encima del mínimo.
 *
 * Cada uno lleva **forma, letra y color a la vez** (principio IV), y el estado de seleccionado se
 * marca además con un anillo y con texto para el lector de pantalla: nunca solo por color.
 */

import { useTranslation } from 'react-i18next';
import { ANSWER_TOKENS, type QuestionOptionView } from '@clica/shared';
import { FichaOpcion, letterFor } from './ficha-opcion.js';
import { Glifo } from './sistema/glifo.js';
import { tamanoSegunTexto } from './escala-opcion.js';
import { ContenidoMarkdown } from '../markdown/contenido.js';

export function BotonesRespuesta({
  options,
  chosen,
  correctOptionId,
  disabled,
  onChoose,
}: {
  options: readonly QuestionOptionView[];
  chosen: string | null;
  /** Solo tras la revelación. Antes es nulo, porque el servidor no lo ha mandado. */
  correctOptionId: string | null;
  disabled: boolean;
  onChoose: (optionId: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <ul className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-3">
      {options.map((option, index) => {
        const token = ANSWER_TOKENS[index];
        const letter = letterFor(index);
        const isChosen = chosen === option.id;
        const isCorrect = correctOptionId === option.id;
        const revealed = correctOptionId !== null;

        return (
          <li key={option.id} className="min-h-0">
            <button
              type="button"
              /*
               * `aria-disabled` y no `disabled`.
               *
               * Un botón deshabilitado sale del orden de tabulación, así que al responder el foco
               * se caía al `body` —justo en el instante decisivo— y quien navega con teclado se
               * quedaba sin punto de referencia. Con `aria-disabled` el botón sigue enfocable, se
               * anuncia como no disponible, y la pulsación se ignora aquí.
               */
              aria-disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onChoose(option.id);
              }}
              aria-pressed={isChosen}
              // El tamaño mínimo de 44 px lo garantiza la cuadrícula, pero se declara igualmente:
              // una pantalla muy baja no puede encoger el objetivo por debajo de eso.
              /*
               * Al revelar, las incorrectas pierden el fondo en lugar de atenuarse: bajar la
               * opacidad arrastra el texto y lo deja por debajo del contraste que exige AA. Aquí
               * además se conserva el anillo de la que eligió esta persona, para que siga sabiendo
               * cuál pulsó aunque fuera la equivocada.
               */
              className={[
                'rounded-tile relative flex size-full min-h-11 items-center justify-center',
                // El relleno cede con la altura de la pantalla: con el móvil en horizontal, un
                // `py-12` fijo dejaba dos de los cuatro botones fuera de la vista.
                'px-4 py-[clamp(0.5rem,4vh,3rem)]',
                'text-option font-bold',
                /*
                 * Dos transiciones con tiempos distintos, y no es capricho.
                 *
                 * El hundido al pulsar va a `--dur-tap` (90 ms): más lento se siente pastoso, y
                 * este botón se pulsa cuarenta veces por pregunta. El cambio de color y de borde
                 * de la revelación va a `--dur-state` (180 ms) con `--ease-mech`, que es lo justo
                 * para que el ojo siga cuál se apaga y cuál se queda; instantáneo, la revelación
                 * es un parpadeo y no se sabe qué ha cambiado.
                 */
                'ease-hard transition-[transform,background-color,border-color] duration-(--dur-state)',
                // **Solo se hunde si la pulsación cuenta.** Hundirse cuando se ignora es
                // prometer que ha pasado algo: quien ya respondió pulsaba de nuevo creyendo que
                // podía cambiar.
                disabled ? '' : 'active:scale-[0.965] active:duration-(--dur-tap)',
                /*
                 * **Filete de tinta SIEMPRE, y por eso el estado se distingue por grosor.**
                 *
                 * Contra el papel, las cuatro fichas dan 4,89 · 3,64 · 3,22 y 2,12 a uno: la D no
                 * llega al 3:1 que WCAG 1.4.11 exige al límite de un componente interactivo, y la
                 * C se queda en el filo. Un borde transparente, que es lo que había, dejaba los
                 * cuatro objetos más grandes de la pantalla sin límite ninguno.
                 *
                 * Con el filete puesto en las cuatro, «la que he elegido» ya no puede ser «la que
                 * tiene borde». Se distingue por grosor —de 2 a 6 px— y por la marca de la
                 * esquina, que es forma y no color. En un móvil viejo al sol, un anillo fino y una
                 * opacidad del 90 % son indistinguibles; seis píxeles de tinta, no.
                 */
                'border-edge border-2',
                isChosen ? 'border-[6px]' : '',
                revealed && isCorrect ? 'border-ok border-[6px]' : '',
                revealed && !isCorrect ? 'border-line' : '',
              ].join(' ')}
              /*
               * Una sola tinta para las cuatro.
               *
               * Con el color de texto medido por ficha, la azul salía en blanco y las otras tres
               * en negro, y eso se lee como un descuido. Las cuatro con tinta dan 3,78 · 5,06 ·
               * 5,72 y 8,69 a uno, todas por encima del 3:1 que WCAG exige al texto grande, que
               * es lo que es: negrita por encima de 19 px. Al revés no se podía: con blanco en
               * las cuatro, la amarilla daría 2,25:1 y sería ilegal.
               */
              style={
                revealed && !isCorrect
                  ? undefined
                  : { backgroundColor: token?.color, color: 'var(--color-on-answer)' }
              }
            >
              {/*
               * **La letra va PRIMERO en el nombre accesible.** La ficha es decorativa, así que
               * sin este texto el botón se anunciaría solo por el contenido de la opción y quien
               * usa lector de pantalla no podría seguir a la profesora cuando dice «la B». Con la
               * letra delante se anuncia «B, oxígeno», que es como se habla en clase.
               */}
              <span className="sr-only">{letter}</span>
              {/* La ficha se retira a la esquina: dice de qué opción se trata sin disputarle el
                  sitio al texto, que es lo que hay que leer para decidir. La forma sigue estando,
                  y con ella la lectura en escala de grises. */}
              <FichaOpcion
                position={index}
                size={30}
                sobreSuColor={!revealed || isCorrect}
                className="absolute top-3 left-3"
              />
              {/* El texto de una opción admite el mismo Markdown que el resto: código en línea,
                  negrita y fórmulas. Se sanea al renderizar (§9.2). */}
              {/* Sin recorte a tres líneas: el texto envuelve y el botón crece. Recortar pierde
                  contenido que no se puede recuperar, y al 200 % de zoom se pierde casi todo. */}
              {/* El texto se encoge por tramos; el recuadro no se mueve. */}
              <span
                className="block text-center leading-snug"
                style={{ fontSize: tamanoSegunTexto(option.textMd) }}
              >
                <ContenidoMarkdown source={option.textMd} enLinea />
              </span>
              {/* La marca de «esta es la mía»: una pastilla de tinta en la esquina opuesta a la
                  ficha. Es forma y posición, así que sobrevive a la escala de grises y a un sol
                  de mediodía sobre la pantalla. */}
              {isChosen && (
                <span
                  aria-hidden="true"
                  className="bg-edge veredicto absolute top-2 right-2 flex size-7 items-center justify-center rounded-full"
                >
                  <Glifo nombre="correcto" size={16} className="text-ground" />
                </span>
              )}
              <span className="sr-only">
                {isChosen ? t('game.question.yourAnswer', { letter }) : ''}
                {revealed && isCorrect ? ` — ${t('game.reveal.wasCorrect', { letter })}` : ''}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
